/**
 * Shared field types (BAL-1202). Only fields that several schemas really share
 * live here: each one fixes a rule the migration found by hand, and none takes a
 * label, because a label would turn into an English message. Messages come from
 * the zod configuration (zod-config.ts).
 *
 * Import as `fields` and write `fields.id`, `fields.optionalString`, ...
 */
import { z } from 'zod';

const id = z.guid();

// A key that may be absent. It does not accept null: use
// `.nullable().optional()` for a nullable key (zod 4 marks a key optional only
// when `.optional()` is outermost).
const optionalString = z.string().optional();

const latitude = z.number().min(-90).max(90);
const longitude = z.number().min(-180).max(180);

export const fields = {
  id,
  email: z.email(),
  emailOrEmpty: z.email().or(z.literal('')),
  urlOrEmpty: z.url().or(z.literal('')),
  password: z.string().min(8),

  /** Required text with an upper bound. The empty string fails as "required". */
  requiredString: z.string().min(1),
  requiredStringWithMax: (max: number) => z.string().min(1).max(max),
  nameField: z.string().min(1).max(255),
  titleField: z.string().min(1).max(255),

  optionalString,
  optionalCountry: z.string().optional(),
  city: z.string().max(255).optional(),
  stateProvince: z.string().max(255).optional(),
  postalCode: z.string().max(20).optional(),
  address: z.string().max(500).optional(),

  isoDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'validation:invalidDate'),
  optionalLatitude: latitude.optional(),
  optionalLongitude: longitude.optional(),

  optionalBoolean: z.boolean().optional(),
  booleanWithDefault: (defaultValue: boolean) =>
    z.boolean().default(defaultValue),
  positiveInteger: z.int().positive(),
  nonEmptyArray: <T extends z.ZodType>(schema: T) => z.array(schema).min(1),
};
